import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Full-size image viewer.
 *
 * Opens in "fit" mode — the whole image scaled down to sit inside the viewport,
 * so nothing is cropped and nothing is off-screen. Clicking the image (or the
 * zoom button) switches to "actual" mode: the image renders at its true pixel
 * size and the frame scrolls, letting the user pan around to read a product
 * label. Clicking again returns to fit.
 *
 * The zoom affordance is only offered when the image is genuinely larger than
 * the frame. For a small photo, 1:1 would be a *downscale* — offering a "zoom"
 * that shrinks the picture reads as a bug, so we suppress it.
 *
 * Generic on purpose: it takes a src and nothing else, so the same viewer can
 * be wired to the admin Catalog thumbnails and the Media Library tiles later
 * without changing anything here.
 */
export function Lightbox({ src, alt, caption, onClose }: {
  src: string;
  alt?: string;
  caption?: string;
  onClose: () => void;
}) {
  const [actual, setActual] = useState(false);
  const [zoomable, setZoomable] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Esc closes. Capture phase so this wins over any parent modal's own handler.
  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Decide whether zooming is worth offering. In fit mode clientWidth/Height is
  // the on-screen (scaled) size, so natural > client means it was downscaled.
  const onLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setZoomable(img.naturalWidth > img.clientWidth + 1 || img.naturalHeight > img.clientHeight + 1);
  }, []);

  // Entering actual size, start the user in the middle of the image rather than
  // pinned to the top-left corner.
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el || !actual) return;
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
  }, [actual]);

  const toggle = () => { if (zoomable) setActual((v) => !v); };

  return (
    <div className="lightbox-bg" onClick={onClose} role="dialog" aria-modal="true" aria-label={alt || 'Image preview'}>
      <div className="lightbox" onClick={(e) => e.stopPropagation()}>
        <div
          ref={frameRef}
          className={`lightbox-frame ${actual ? 'actual' : 'fit'}${zoomable ? ' zoomable' : ''}`}
        >
          <img src={src} alt={alt || ''} onLoad={onLoad} onClick={toggle} />
        </div>

        <div className="lightbox-bar">
          {caption && <span className="lightbox-caption">{caption}</span>}
          <span className="spacer" />
          {zoomable && (
            <button className="lightbox-btn" onClick={toggle}>
              {actual ? 'Fit to screen' : 'Actual size'}
            </button>
          )}
          <a className="lightbox-btn" href={src} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
            Open original
          </a>
        </div>

        <button ref={closeRef} className="lightbox-close" onClick={onClose} aria-label="Close image">×</button>
      </div>
    </div>
  );
}

/**
 * Wiring helper. A page calls `const lb = useLightbox()`, renders `{lb.node}`
 * once anywhere in its tree, and calls `lb.open(src, alt, caption)` from as many
 * thumbnails as it likes — one piece of state per page instead of one per image.
 */
export function useLightbox() {
  const [shown, setShown] = useState<{ src: string; alt?: string; caption?: string } | null>(null);
  const open = useCallback((src: string, alt?: string, caption?: string) => {
    setShown({ src, alt, caption });
  }, []);
  const node = shown
    ? <Lightbox src={shown.src} alt={shown.alt} caption={shown.caption} onClose={() => setShown(null)} />
    : null;
  return { open, node };
}

/**
 * A thumbnail that opens the lightbox when clicked. `className` is passed
 * through to the <img>, so each call site keeps its existing thumbnail styling
 * (.row-thumb, .photo-thumb, …) and only gains the click behaviour.
 *
 * stopPropagation matters: these sit inside table cells and modals that have
 * their own click handlers.
 */
export function ZoomThumb({ src, alt, caption, className, block, onOpen }: {
  src: string;
  alt?: string;
  caption?: string;
  className?: string;
  /** Fill the container (media-library cards) rather than sit inline (table cells). */
  block?: boolean;
  onOpen: (src: string, alt?: string, caption?: string) => void;
}) {
  return (
    <button
      type="button"
      className={`zoom-thumb${block ? ' block' : ''}`}
      title="Click to see the full photo"
      aria-label={caption ? `View full photo of ${caption}` : 'View full photo'}
      onClick={(e) => { e.stopPropagation(); onOpen(src, alt, caption); }}
    >
      <img className={className} src={src} alt={alt || ''} />
    </button>
  );
}
